import { useFetchADPCredentialsQuery } from '@/redux/slices/hrisIntegrationApiSlice';
import { useAppSelector } from '@/redux/store';
import { HRISProviderSlugEnum } from '@electricjs/core_entity-client';

import ADPConsentRequiredBanner from './ADPConsentRequiredBanner';

// Constants
const ADP_PROVIDER_TYPES = [
  HRISProviderSlugEnum.AdpWfn,
  HRISProviderSlugEnum.AdpR,
  HRISProviderSlugEnum.AdpWfnNg,
  HRISProviderSlugEnum.AdpWfnCanada,
  HRISProviderSlugEnum.AdpWfnTotalSource,
] as const;

const ADP_CONSUMER_APP_IDS = {
  [HRISProviderSlugEnum.AdpR]:
    window._env_?.VITE_ADP_RUN_CONSUMER_APPLICATION_ID,
  [HRISProviderSlugEnum.AdpWfn]:
    window._env_?.VITE_ADP_WFN_CONSUMER_APPLICATION_ID,
  [HRISProviderSlugEnum.AdpWfnNg]:
    window._env_?.VITE_ADP_WFN_NG_CONSUMER_APPLICATION_ID,
  [HRISProviderSlugEnum.AdpWfnCanada]:
    window._env_?.VITE_ADP_WFN_CANADA_CONSUMER_APPLICATION_ID,
  [HRISProviderSlugEnum.AdpWfnTotalSource]:
    window._env_?.VITE_ADP_WFN_TOTAL_SOURCE_CONSUMER_APPLICATION_ID,
} as const;

// Utility functions
const isADPProvider = (
  providerSlug: string | undefined
): providerSlug is (typeof ADP_PROVIDER_TYPES)[number] => {
  if (!providerSlug) return false;
  return ADP_PROVIDER_TYPES.some(type => type === providerSlug);
};

const buildADPConsentURL = (
  successUri: string,
  providerType: (typeof ADP_PROVIDER_TYPES)[number]
): string => {
  const consumerAppId =
    ADP_CONSUMER_APP_IDS[providerType] ||
    ADP_CONSUMER_APP_IDS[HRISProviderSlugEnum.AdpWfn];
  return `https://adpapps.adp.com/consent-manager/pending/direct?consumerApplicationID=${consumerAppId}&successUri=${successUri}`;
};

const ADPConsentRequiredBannerWrapper = () => {
  const loggedUser = useAppSelector(state => state.loggedUser);
  const hrisIntegration = loggedUser?.organizationHrisIntegration;

  // Check if we have the required data and conditions
  const hasRequiredData = Boolean(
    loggedUser?.organizationId &&
      hrisIntegration?.id &&
      hrisIntegration?.hrisProvider?.slug
  );

  const providerSlug = hrisIntegration?.hrisProvider?.slug;
  const isADPOrg = hasRequiredData && isADPProvider(providerSlug);
  const isPurchaser =
    hasRequiredData && loggedUser?.id === hrisIntegration?.createdBy;
  const shouldShowBanner = isADPOrg && isPurchaser;

  const { data: adpCredentialsExist } = useFetchADPCredentialsQuery(
    {
      organizationId: loggedUser?.organizationId ?? '',
      hrisIntegrationId: hrisIntegration?.id ?? '',
    },
    {
      skip: !shouldShowBanner,
    }
  );

  // Only show the banner if the query ran and credentials don't exist (false).
  if (!shouldShowBanner || adpCredentialsExist !== false) {
    return null;
  }

  const consentRedirectURL = buildADPConsentURL(
    encodeURIComponent(window.location.href),
    providerSlug
  );

  return (
    <ADPConsentRequiredBanner adpConsentRedirectURL={consentRedirectURL} />
  );
};

export default ADPConsentRequiredBannerWrapper;
